import {
  Modal,
  Stack,
  Select,
  TextInput,
  Box,
  Paper,
  Group,
  NumberInput,
  Textarea,
  Button,
  Text,
  MultiSelect,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconBoxPadding } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { notifications } from "@mantine/notifications";
import {
  getContractCrewsByPerception,
  getContractMachinesByPerception,
  upsertObjectDiary,
  getDiaryTemplateOrInfo,
} from "../../../../services/objectmodelservice";
import { UpsertObjectDiaryRequest } from "../../../../types/UpsertObjectDiaryRequest";
import { useTranslation } from "../../../../hooks/useTranslation";
import useTransientStore from "../../../../data/transcientStore";
import {
  parseDiaryItemList,
  serializeDiaryItemList,
  parseOptionalInt,
} from "../../../../utilities/Utilities";

export interface DiaryEntryForm {
  model: number | null;
  date: Date | null;
  weather: string;
  valvoja: string;
  tyonjohtaja: string;
  tyonjohtaja2: string;
  workingGroup: string;
  equipment: string;
  diesel: number;
  mpo: number;
  jobRemarks: string;
  clientRemarks: string;
  notesToClient: string;
  others: string;
}

interface AddEditDiaryEntryModalProps {
  opened: boolean;
  onClose: () => void;
  formValues: DiaryEntryForm;
  setFormValues: (values: DiaryEntryForm) => void;
  onSave: (result?: { id: number; created: boolean }) => void;
  title?: string;
  templateOptions?: { value: number; label: string }[];
  supervisorOptions?: { value: string; label: string }[];
  foremanOptions?: { value: string; label: string }[];
  foreman2Options?: { value: string; label: string }[];
  objectId?: number; // perception/object id for fetching crews/machines
  contractId?: number; // contract id required for save
  diaryId?: number; // optional when editing existing diary
  currentUserId?: number; // optional; used to satisfy backend creator requirement
  addressSystemId?: number; // needed to fetch template details
}

export default function AddEditDiaryEntryModal({
  opened,
  onClose,
  formValues,
  setFormValues,
  onSave,
  title,
  templateOptions = [],
  supervisorOptions = [],
  foremanOptions = [],
  foreman2Options = [],
  objectId,
  contractId,
  diaryId,
  currentUserId,
  addressSystemId,
}: AddEditDiaryEntryModalProps) {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem
  );
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 313,
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });

  const resolvedTitle =
    title || getTranslation("TITLE_ADD_A_JOURNAL_ENTRY") || "Add a Journal Entry";
  // Local state for working group dual-list behavior
  const [availableItems, setAvailableItems] = useState<string[]>([]);
  // Loaded list of groups (names) from backend for current source
  const [groupItems, setGroupItems] = useState<
    { value: string; label: string; description: string }[]
  >([]);
  const [selectedGroupItemId, setSelectedGroupItemId] = useState<string>("");
  const [selectedItems, setSelectedItems] = useState<string[]>(() =>
    parseDiaryItemList(formValues.workingGroup)
  );
  const [pendingAdd, setPendingAdd] = useState<string>("");
  const [pendingRemove, setPendingRemove] = useState<string>("");
  const [pendingAddMany, setPendingAddMany] = useState<string[]>([]);
  const [pendingRemoveMany, setPendingRemoveMany] = useState<string[]>([]);
  // Equipment dual-list local state
  const [equipmentAvailable, setEquipmentAvailable] = useState<string[]>([]);
  const [equipmentSelected, setEquipmentSelected] = useState<string[]>(() =>
    parseDiaryItemList(formValues.equipment)
  );
  const [pendingEquipAddMany, setPendingEquipAddMany] = useState<string[]>([]);
  const [pendingEquipRemoveMany, setPendingEquipRemoveMany] = useState<
    string[]
  >([]);
  // Equipment group selection (machines from backend)
  const [equipmentGroups, setEquipmentGroups] = useState<
    { value: string; label: string; description: string }[]
  >([]);
  const [selectedEquipmentGroupId, setSelectedEquipmentGroupId] =
    useState<string>("");

  // Reset Available lists and Group Source selections when modal closes or objectId changes
  // to prevent state from one object leaking into another.
  useEffect(() => {
    if (!opened) {
      setAvailableItems([]);
      setEquipmentAvailable([]);
      setSelectedGroupItemId("");
      setSelectedEquipmentGroupId("");
    }
  }, [opened, objectId]);

  const handleSave = async () => {
    if (!objectId || !contractId) {
      onSave();
      return;
    }
    const payload: UpsertObjectDiaryRequest = {
      id: diaryId,
      contractId: contractId,
      objectId: objectId,
      diaryDate: formValues.date,
      weather: formValues.weather || null,
      supervisor: formValues.valvoja || null,
      foreman: formValues.tyonjohtaja || null,
      frontman: formValues.tyonjohtaja2 || null,
      workers: formValues.workingGroup || null,
      machines: formValues.equipment || null,
      tasks: formValues.jobRemarks || null,
      subscriberNotices: formValues.clientRemarks || null,
      noticesToSubscriber: formValues.notesToClient || null,
      description: formValues.others || null,
      supervisorId: parseOptionalInt(formValues.valvoja),
      foremanId: parseOptionalInt(formValues.tyonjohtaja),
      viseForemanId: parseOptionalInt(formValues.tyonjohtaja2),
      dieselConsumption: formValues.diesel ?? 0,
      mpoConsumption: formValues.mpo ?? 0,
      diaryStatus: 0,
      checkReturn: false,
    };
    try {
      const result = await upsertObjectDiary(payload, currentUserId);
      notifications.show({
        title: result.created
          ? getTranslation("TITLE_DIARY_CREATED") || "Diary Created"
          : getTranslation("TITLE_DIARY_UPDATED") || "Diary Updated",
        message: result.created
          ? getTranslation("MESSAGE_DIARY_CREATED") ||
            "A new diary entry has been created."
          : getTranslation("MESSAGE_DIARY_UPDATED") ||
            "Diary entry updated successfully.",
        color: "green",
        autoClose: 4000,
        position: "top-center",
      });
      onSave({ id: result.id as any, created: (result as any).created });
    } catch (e) {
      notifications.show({
        title: getTranslation("TITLE_SAVE_FAILED") || "Save failed",
        message:
          e instanceof Error
            ? e.message
            : getTranslation("MESSAGE_SAVE_DIARY_ENTRY_FAILED") ||
              "Unable to save diary entry",
        color: "red",
        autoClose: 5000,
        position: "top-center",
      });
      // Do not close/propagate on error; keep modal open for correction
    }
  };

  // Sync dual-list "Selected" state from formValues when the modal opens.
  // useState initializers only run once on mount, so without this effect an
  // Edit on an already-mounted modal would not preselect items.
  useEffect(() => {
    if (!opened) return;
    setSelectedItems(parseDiaryItemList(formValues.workingGroup));
    setEquipmentSelected(parseDiaryItemList(formValues.equipment));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // Load crew lists (names) from backend for Working group (no source condition)
  useEffect(() => {
    let cancelled = false;
    async function load() {
      // Reset when not applicable
      if (!opened || !objectId || objectId <= 0) {
        setGroupItems([]);
        setSelectedGroupItemId("");
        setAvailableItems([]);
        return;
      }

      try {
        const list = await getContractCrewsByPerception(objectId);
        if (cancelled) return;
        setGroupItems(
          (list || []).map((x) => ({
            value: String(x.id),
            label: x.name,
            description: x.description || "",
          }))
        );
        setSelectedGroupItemId("");
        setAvailableItems([]);
      } catch (err) {
        setGroupItems([]);
        setSelectedGroupItemId("");
        setAvailableItems([]);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [opened, objectId]);

  // When a group is chosen, parse description lines into available items
  useEffect(() => {
    const chosen = groupItems.find((g) => g.value === selectedGroupItemId);
    if (!chosen) {
      setAvailableItems([]);
      return;
    }
    const lines = (chosen.description || "")
      .replace(/\r\n/g, "\n")
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const remaining = lines.filter((x) => !selectedItems.includes(x));
    setAvailableItems(remaining);
  }, [selectedGroupItemId, groupItems, selectedItems]);

  // Load equipment groups (machines) - no source condition
  useEffect(() => {
    let cancelled = false;
    async function loadMachines() {
      if (!opened || !objectId || objectId <= 0) {
        setEquipmentGroups([]);
        setSelectedEquipmentGroupId("");
        setEquipmentAvailable([]);
        return;
      }
      try {
        const list = await getContractMachinesByPerception(objectId);
        if (cancelled) return;
        setEquipmentGroups(
          (list || []).map((x) => ({
            value: String(x.id),
            label: x.name,
            description: x.description || "",
          }))
        );
        setSelectedEquipmentGroupId("");
        setEquipmentAvailable([]);
      } catch {
        setEquipmentGroups([]);
        setSelectedEquipmentGroupId("");
        setEquipmentAvailable([]);
      }
    }
    loadMachines();
    return () => {
      cancelled = true;
    };
  }, [opened, objectId]);

  // When an equipment group is chosen, parse description into equipmentAvailable
  useEffect(() => {
    const chosen = equipmentGroups.find(
      (g) => g.value === selectedEquipmentGroupId
    );
    if (!chosen) {
      setEquipmentAvailable([]);
      return;
    }
    const lines = (chosen.description || "")
      .replace(/\r\n/g, "\n")
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const remaining = lines.filter((x) => !equipmentSelected.includes(x));
    setEquipmentAvailable(remaining);
  }, [selectedEquipmentGroupId, equipmentGroups, equipmentSelected]);

  // When template changes, clear fields first then load from selected template
  const handleTemplateChange = async (value: string | null) => {
    const nextModel =
      value && value.length > 0 && !Number.isNaN(Number(value))
        ? Number(value)
        : null;
    // Clear all form fields immediately and set selected template (model)
    const clearedForm: DiaryEntryForm = {
      model: nextModel,
      date: new Date(),
      weather: "",
      valvoja: "",
      tyonjohtaja: "",
      tyonjohtaja2: "",
      workingGroup: "",
      equipment: "",
      diesel: 0,
      mpo: 0,
      jobRemarks: "",
      clientRemarks: "",
      notesToClient: "",
      others: "",
    };
    setFormValues(clearedForm);
    // Also reset dual-list selections and any pending selections
    setSelectedItems([]);
    setEquipmentSelected([]);
    setPendingAddMany([]);
    setPendingRemoveMany([]);
    setPendingEquipAddMany([]);
    setPendingEquipRemoveMany([]);

    // If no concrete template is selected or missing IDs, stop after clearing
    if (!nextModel || !addressSystemId || !objectId) return;
    try {
      const info = await getDiaryTemplateOrInfo(
        addressSystemId,
        objectId,
        nextModel,
        currentUserId || 0
      );
      const dateVal = (info as any)?.diaryDate
        ? new Date((info as any).diaryDate)
        : undefined;
      const weatherVal = (info as any)?.weather;
      const supervisorIdVal = (info as any)?.supervisorId;
      const foremanIdVal = (info as any)?.foremanId;
      const viseForemanIdVal = (info as any)?.viseForemanId;
      const wgString = (info as any)?.workers as string | undefined;
      const eqString = (info as any)?.machines as string | undefined;
      const tasksVal = (info as any)?.tasks;
      const subNoticesVal = (info as any)?.subscriberNotices;
      const notesVal = (info as any)?.noticesToSubscriber;
      const descVal = (info as any)?.description;
      const dieselVal = (info as any)?.dieselConsumption;
      const mpoVal = (info as any)?.mpoConsumption;

      const mergedForm = {
        ...clearedForm,
        model: nextModel,
        date: dateVal ?? clearedForm.date,
        weather: weatherVal ?? clearedForm.weather,
        valvoja:
          typeof supervisorIdVal === "number" && supervisorIdVal > 0
            ? String(supervisorIdVal)
            : clearedForm.valvoja,
        tyonjohtaja:
          typeof foremanIdVal === "number" && foremanIdVal > 0
            ? String(foremanIdVal)
            : clearedForm.tyonjohtaja,
        tyonjohtaja2:
          typeof viseForemanIdVal === "number" && viseForemanIdVal > 0
            ? String(viseForemanIdVal)
            : clearedForm.tyonjohtaja2,
        workingGroup: wgString ?? clearedForm.workingGroup,
        equipment: eqString ?? clearedForm.equipment,
        jobRemarks: tasksVal ?? clearedForm.jobRemarks,
        clientRemarks: subNoticesVal ?? clearedForm.clientRemarks,
        notesToClient: notesVal ?? clearedForm.notesToClient,
        others: descVal ?? clearedForm.others,
        diesel: typeof dieselVal === "number" ? dieselVal : clearedForm.diesel,
        mpo: typeof mpoVal === "number" ? mpoVal : clearedForm.mpo,
      } as DiaryEntryForm;
      setFormValues(mergedForm);

      // Sync dual-list selections only if strings provided
      if (typeof mergedForm.workingGroup === "string") {
        setSelectedItems(parseDiaryItemList(mergedForm.workingGroup));
      }
      if (typeof mergedForm.equipment === "string") {
        setEquipmentSelected(parseDiaryItemList(mergedForm.equipment));
      }
    } catch (err) {
      notifications.show({
        title: "Template load failed",
        message: err instanceof Error ? err.message : "Unable to load template",
        color: "red",
        autoClose: 4000,
        position: "top-center",
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={resolvedTitle}
      size="lg"
      centered
    >
      <Stack gap="md">
        {!diaryId && (
          <Select
            label={getTranslation("LABEL_TEMPLATE") || "Template"}
            value={formValues.model != null ? String(formValues.model) : ""}
            onChange={handleTemplateChange}
            data={
              templateOptions.length > 0
                ? [
                    {
                      value: "",
                      label:
                        getTranslation("LABEL_SELECT_TEMPLATE_OPTION") ||
                        "Select Template",
                    },
                    ...templateOptions.map((o) => ({
                      value: String(o.value),
                      label: o.label,
                    })),
                  ]
                : [
                    {
                      value: "",
                      label:
                        getTranslation("LABEL_SELECT_TEMPLATE_OPTION") ||
                        "Select Template",
                    },
                  ]
            }
            placeholder={getTranslation("PLACEHOLDER_SELECT_TEMPLATE") || "Select Template"}
          />
        )}

        <DatePickerInput
          label={getTranslation("LABEL_DATE") || "Date"}
          required
          value={formValues.date}
          onChange={(date) => setFormValues({ ...formValues, date })}
          placeholder={getTranslation("PLACEHOLDER_SELECT_DATE") || "Select date"}
          valueFormat="DD.MM.YYYY"
        />

        <TextInput
          label={getTranslation("LABEL_WEATHER_AND_TEMPERATURE") || "Weather and temperature"}
          value={formValues.weather}
          onChange={(e) =>
            setFormValues({ ...formValues, weather: e.target.value })
          }
          placeholder={getTranslation("PLACEHOLDER_ENTER_WEATHER_CONDITIONS") || "Enter weather conditions"}
        />

        <Box>
          <Text fw={500} mb="sm">
            {getTranslation("LABEL_DIARY_PERSONNEL_EQUIPMENT") ||
              "Personnel and equipment"}
          </Text>
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Select
                label={getTranslation("LABEL_SUPERVISOR") || "Supervisor"}
                value={formValues.valvoja || null}
                onChange={(value) =>
                  setFormValues({ ...formValues, valvoja: value || "" })
                }
                data={
                  supervisorOptions.length > 0
                    ? [
                        {
                          value: "",
                          label:
                            getTranslation("LABEL_SELECT_SUPERVISOR_OPTION") ||
                            "Select Supervisor",
                        },
                        ...supervisorOptions,
                      ]
                    : [
                        {
                          value: "",
                          label:
                            getTranslation("LABEL_SELECT_SUPERVISOR_OPTION") ||
                            "Select Supervisor",
                        },
                      ]
                }
                placeholder={getTranslation("PLACEHOLDER_SELECT_SUPERVISOR") || "Select Supervisor"}
              />
              <Select
                label={getTranslation("LABEL_FOREMAN") || "Foreman"}
                value={formValues.tyonjohtaja || null}
                onChange={(value) =>
                  setFormValues({ ...formValues, tyonjohtaja: value || "" })
                }
                data={
                  foremanOptions.length > 0
                    ? [
                        {
                          value: "",
                          label:
                            getTranslation("LABEL_SELECT_FOREMAN_OPTION") ||
                            "Select Foreman",
                        },
                        ...foremanOptions,
                      ]
                    : [
                        {
                          value: "",
                          label:
                            getTranslation("LABEL_SELECT_FOREMAN_OPTION") ||
                            "Select Foreman",
                        },
                      ]
                }
                placeholder={getTranslation("PLACEHOLDER_SELECT_FOREMAN") || "Select Foreman"}
              />
              <Select
                label={getTranslation("LABEL_FOREMAN_2") || "Foreman 2"}
                value={formValues.tyonjohtaja2 || null}
                onChange={(value) =>
                  setFormValues({ ...formValues, tyonjohtaja2: value || "" })
                }
                data={
                  foreman2Options.length > 0
                    ? [
                        {
                          value: "",
                          label:
                            getTranslation("LABEL_SELECT_FOREMAN_2_OPTION") ||
                            "Select Foreman 2",
                        },
                        ...foreman2Options,
                      ]
                    : [
                        {
                          value: "",
                          label:
                            getTranslation("LABEL_SELECT_FOREMAN_2_OPTION") ||
                            "Select Foreman 2",
                        },
                      ]
                }
                placeholder={getTranslation("PLACEHOLDER_SELECT_FOREMAN_2") || "Select Foreman 2"}
              />
            </Stack>
          </Paper>
        </Box>

        {/* Working group modern dual-list selector */}
        <Box>
          <Text fw={500} mb="sm">
            {getTranslation("LABEL_DIARY_WORKING_GROUP") || "Working group"}
          </Text>
          <Paper p="md" withBorder>
            <Select
              mt="xs"
              label={getTranslation("LABEL_GROUP_SOURCE") || "Group Source"}
              placeholder={getTranslation("PLACEHOLDER_CHOOSE_SOURCE") || "Choose source"}
              data={[
                {
                  value: "",
                  label:
                    getTranslation("LABEL_CHOOSE_SOURCE_OPTION") ||
                    "Choose source",
                },
                ...groupItems.map((g) => ({ value: g.value, label: g.label })),
              ]}
              value={selectedGroupItemId}
              onChange={(val) => setSelectedGroupItemId(val || "")}
              searchable
            />
            <Group align="start" mt="md" grow>
              <Stack gap="xs">
                <Text size="sm">
                  {getTranslation("LABEL_AVAILABLE") || "Available"}
                </Text>
                <MultiSelect
                  data={availableItems.map((x) => ({ value: x, label: x }))}
                  value={pendingAddMany}
                  onChange={setPendingAddMany}
                  searchable
                  placeholder={getTranslation("PLACEHOLDER_CHOOSE_TO_ADD") || "Choose to add"}
                  style={{ paddingBottom: 10 }}
                />
                <Button
                  onClick={() => {
                    if (!pendingAddMany || pendingAddMany.length === 0) return;
                    const merged = Array.from(
                      new Set([...selectedItems, ...pendingAddMany])
                    );
                    setSelectedItems(
                      [...merged].sort((a, b) => a.localeCompare(b))
                    );
                    const remainingAvail = availableItems.filter(
                      (x) => !pendingAddMany.includes(x)
                    );
                    setAvailableItems(
                      [...remainingAvail].sort((a, b) => a.localeCompare(b))
                    );
                    setFormValues({
                      ...formValues,
                      workingGroup: serializeDiaryItemList(merged),
                    });
                    setPendingAddMany([]);
                  }}
                >
                  {getTranslation("LABEL_ADD_ARROW") || "Add →"}
                </Button>
              </Stack>

              <Stack gap="xs">
                <Text size="sm">
                  {getTranslation("LABEL_SELECTED") || "Selected"}
                </Text>
                <MultiSelect
                  data={selectedItems.map((x) => ({ value: x, label: x }))}
                  value={pendingRemoveMany}
                  onChange={setPendingRemoveMany}
                  searchable
                  placeholder={getTranslation("PLACEHOLDER_CHOOSE_TO_REMOVE") || "Choose to remove"}
                />
                <Group gap="xs" wrap="wrap">
                  {selectedItems.map((item) => (
                    <Group
                      key={item}
                      gap={4}
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: 12,
                        padding: "2px 8px",
                      }}
                    >
                      <Text size="sm">{item}</Text>
                      <Button
                        variant="subtle"
                        color="red"
                        size="compact-xs"
                        onClick={() => {
                          const next = selectedItems.filter((x) => x !== item);
                          setSelectedItems(
                            [...next].sort((a, b) => a.localeCompare(b))
                          );
                          if (!availableItems.includes(item)) {
                            setAvailableItems(
                              [...availableItems, item].sort((a, b) =>
                                a.localeCompare(b)
                              )
                            );
                          }
                          setFormValues({
                            ...formValues,
                            workingGroup: serializeDiaryItemList(next),
                          });
                        }}
                      >
                        ×
                      </Button>
                    </Group>
                  ))}
                </Group>
                <Button
                  color="red"
                  onClick={() => {
                    if (!pendingRemoveMany || pendingRemoveMany.length === 0)
                      return;
                    const removeSet = new Set(pendingRemoveMany);
                    const next = selectedItems.filter((x) => !removeSet.has(x));
                    setSelectedItems(
                      [...next].sort((a, b) => a.localeCompare(b))
                    );
                    const restored = pendingRemoveMany.filter(
                      (x) => !availableItems.includes(x)
                    );
                    setAvailableItems(
                      [...availableItems, ...restored].sort((a, b) =>
                        a.localeCompare(b)
                      )
                    );
                    setFormValues({
                      ...formValues,
                      workingGroup: serializeDiaryItemList(next),
                    });
                    setPendingRemoveMany([]);
                  }}
                >
                  {getTranslation("LABEL_REMOVE_ARROW") || "← Remove"}
                </Button>
              </Stack>
            </Group>
          </Paper>
        </Box>

        {/* Equipment modern dual-list selector */}
        <Box>
          <Text fw={500} mb="sm">
            {getTranslation("LABEL_EQUIPMENT_GROUP") || "Equipment Group"}
          </Text>
          <Paper p="md" withBorder>
            <Select
              mt="xs"
              label={getTranslation("LABEL_GROUP_SOURCE") || "Group Source"}
              placeholder={getTranslation("PLACEHOLDER_CHOOSE_SOURCE") || "Choose source"}
              data={[
                {
                  value: "",
                  label:
                    getTranslation("LABEL_CHOOSE_SOURCE_OPTION") ||
                    "Choose source",
                },
                ...equipmentGroups.map((g) => ({
                  value: g.value,
                  label: g.label,
                })),
              ]}
              value={selectedEquipmentGroupId}
              onChange={(val) => setSelectedEquipmentGroupId(val || "")}
              searchable
            />
            <Group align="start" mt="xs" grow>
              <Stack gap="xs">
                <Text size="sm">
                  {getTranslation("LABEL_AVAILABLE") || "Available"}
                </Text>
                <MultiSelect
                  data={equipmentAvailable.map((x) => ({ value: x, label: x }))}
                  value={pendingEquipAddMany}
                  onChange={setPendingEquipAddMany}
                  searchable
                  placeholder={getTranslation("PLACEHOLDER_CHOOSE_TO_ADD") || "Choose to add"}
                  style={{ paddingBottom: 10 }}
                />
                <Button
                  onClick={() => {
                    if (
                      !pendingEquipAddMany ||
                      pendingEquipAddMany.length === 0
                    )
                      return;
                    const merged = Array.from(
                      new Set([...equipmentSelected, ...pendingEquipAddMany])
                    );
                    setEquipmentSelected(
                      [...merged].sort((a, b) => a.localeCompare(b))
                    );
                    const remainingAvail = equipmentAvailable.filter(
                      (x) => !pendingEquipAddMany.includes(x)
                    );
                    setEquipmentAvailable(
                      [...remainingAvail].sort((a, b) => a.localeCompare(b))
                    );
                    setFormValues({
                      ...formValues,
                      equipment: serializeDiaryItemList(merged),
                    });
                    setPendingEquipAddMany([]);
                  }}
                >
                  {getTranslation("LABEL_ADD_ARROW") || "Add →"}
                </Button>
              </Stack>

              <Stack gap="xs">
                <Text size="sm">
                  {getTranslation("LABEL_SELECTED") || "Selected"}
                </Text>
                <MultiSelect
                  data={equipmentSelected.map((x) => ({ value: x, label: x }))}
                  value={pendingEquipRemoveMany}
                  onChange={setPendingEquipRemoveMany}
                  searchable
                  placeholder={getTranslation("PLACEHOLDER_CHOOSE_TO_REMOVE") || "Choose to remove"}
                />
                <Group gap="xs" wrap="wrap">
                  {equipmentSelected.map((item) => (
                    <Group
                      key={item}
                      gap={4}
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: 12,
                        padding: "2px 8px",
                      }}
                    >
                      <Text size="sm">{item}</Text>
                      <Button
                        variant="subtle"
                        color="red"
                        size="compact-xs"
                        onClick={() => {
                          const next = equipmentSelected.filter(
                            (x) => x !== item
                          );
                          setEquipmentSelected(
                            [...next].sort((a, b) => a.localeCompare(b))
                          );
                          if (!equipmentAvailable.includes(item)) {
                            setEquipmentAvailable(
                              [...equipmentAvailable, item].sort((a, b) =>
                                a.localeCompare(b)
                              )
                            );
                          }
                          setFormValues({
                            ...formValues,
                            equipment: serializeDiaryItemList(next),
                          });
                        }}
                      >
                        ×
                      </Button>
                    </Group>
                  ))}
                </Group>
                <Button
                  color="red"
                  onClick={() => {
                    if (
                      !pendingEquipRemoveMany ||
                      pendingEquipRemoveMany.length === 0
                    )
                      return;
                    const removeSet = new Set(pendingEquipRemoveMany);
                    const next = equipmentSelected.filter(
                      (x) => !removeSet.has(x)
                    );
                    setEquipmentSelected(
                      [...next].sort((a, b) => a.localeCompare(b))
                    );
                    const restored = pendingEquipRemoveMany.filter(
                      (x) => !equipmentAvailable.includes(x)
                    );
                    setEquipmentAvailable(
                      [...equipmentAvailable, ...restored].sort((a, b) =>
                        a.localeCompare(b)
                      )
                    );
                    setFormValues({
                      ...formValues,
                      equipment: serializeDiaryItemList(next),
                    });
                    setPendingEquipRemoveMany([]);
                  }}
                >
                  {getTranslation("LABEL_REMOVE_ARROW") || "← Remove"}
                </Button>
              </Stack>
            </Group>
          </Paper>
        </Box>

        <Box>
          <Text fw={500} mb="sm">
            {getTranslation("LABEL_FUEL_CONSUMPTION") || "Fuel consumption"}
          </Text>
          <Group grow>
            <NumberInput
              label={getTranslation("LABEL_DIESEL") || "Diesel"}
              value={formValues.diesel}
              onChange={(value) =>
                setFormValues({ ...formValues, diesel: Number(value) || 0 })
              }
              placeholder={getTranslation("PLACEHOLDER_0") || "0"}
            />
            <NumberInput
              label={getTranslation("LABEL_MPO") || "MPO"}
              value={formValues.mpo}
              onChange={(value) =>
                setFormValues({ ...formValues, mpo: Number(value) || 0 })
              }
              placeholder={getTranslation("PLACEHOLDER_0") || "0"}
            />
          </Group>
        </Box>

        <Textarea
          label={getTranslation("LABEL_WORKRELATED_REMARKS_INTERRUPTIONS_ETC") || "Work-related remarks, interruptions, etc."}
          value={formValues.jobRemarks}
          onChange={(e) =>
            setFormValues({ ...formValues, jobRemarks: e.target.value })
          }
          placeholder={getTranslation("PLACEHOLDER_ENTER_JOBRELATED_REMARKS") || "Enter job-related remarks"}
          minRows={3}
        />

        <Textarea
          label={getTranslation("LABEL_SUBSCRIBERS_REMARKS") || "Subscriber's Remarks"}
          value={formValues.clientRemarks}
          onChange={(e) =>
            setFormValues({ ...formValues, clientRemarks: e.target.value })
          }
          placeholder={getTranslation("PLACEHOLDER_ENTER_SUBSCRIBERS_REMARKS") || "Enter Subscriber's remarks"}
          minRows={3}
        />

        <Textarea
          label={getTranslation("LABEL_NOTES_TO_THE_SUBSCRIBER") || "Notes to the subscriber"}
          value={formValues.notesToClient}
          onChange={(e) =>
            setFormValues({ ...formValues, notesToClient: e.target.value })
          }
          placeholder={getTranslation("PLACEHOLDER_ENTER_NOTES_TO_SUBSCRIBER") || "Enter notes to subscriber"}
          minRows={3}
        />

        <Textarea
          label={getTranslation("LABEL_OTHERS") || "Others"}
          value={formValues.others}
          onChange={(e) =>
            setFormValues({ ...formValues, others: e.target.value })
          }
          placeholder={getTranslation("PLACEHOLDER_ENTER_OTHER_NOTES") || "Enter other notes"}
          minRows={3}
        />

        <Group justify="flex-end">
          <Button variant="outline" onClick={onClose}>
            {getTranslation("LABEL_CANCEL") || "Cancel"}
          </Button>
          <Button onClick={handleSave}>
            {getTranslation("LABEL_SAVE") || "Save"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
